import React, { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import KeyboardBackspaceIcon from "@mui/icons-material/KeyboardBackspace";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { useMe } from "../hooks/useMe";
import Sidebar from "../components/Sidebar";
import API from "../APIs/API";
import "../styles/profile.scss";
import "../styles/list.scss";
import { toast } from "react-toastify";
import dayjs from "dayjs";

// ─── ACQUITTAL WORKFLOW STEPS ────────────────────────────────────────────────
// Step 1: Employee submits return (uploads boarding pass)      → notifies Acquittal Officer
// Step 2: Acquittal Officer enters departure/arrival times     → system calculates allowance & refund
//          → notifies Employee to upload receipt
// Step 3: Employee uploads receipt                             → notifies Acquittal Officer
// Step 4: Acquittal Officer reviews line items (Yes/No)        → generates printable report
// Source: Duty Travel.docx — Resume Work from Duty Travel section

// ─── DOMESTIC CALCULATION REFERENCE ─────────────────────────────────────────
// Complete day: K200 | After 21 days: K120 (60%)
// Part day (departure before 7am)   → K200
// Part day (7am–1pm)                → K178
// Part day (1pm–7pm)                → K143
// Part day (after 7pm)              → K95

// ─── OVERSEAS CALCULATION REFERENCE ─────────────────────────────────────────
// Per diem from Approved OSTA rates table (by country, USD)
// After 21 days: 60% of per diem

const StatusBadge = ({ status }) => {
  const map = {
    pending:  { bg: "#fef9c3", color: "#92400e" },
    approved: { bg: "#dcfce7", color: "#166534" },
    declined: { bg: "#fee2e2", color: "#991b1b" },
  };
  const s = map[status?.toLowerCase()] ?? map.pending;
  return (
    <span style={{ fontSize: 12, fontWeight: 700, padding: "3px 12px", borderRadius: 10, backgroundColor: s.bg, color: s.color, textTransform: "capitalize" }}>
      {status ?? "Pending"}
    </span>
  );
};

const DutyTravelView = ({ type }) => {
  const { admin_location_id, duty_travel_id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { permissions, user: loggedUser } = useMe();

  const pageUserType = type === "Teacher" ? "teachers" : "non-teachers";
  const backUrl = `/${pageUserType}/${admin_location_id}/duty-travel`;

  // ── Fetch record ──────────────────────────────────────────────────────────
  const { data: record, isLoading } = useQuery(
    ["duty_travel", duty_travel_id],
    () => new API().getDutyTravelById(duty_travel_id),
    { enabled: !!duty_travel_id }
  );

  const invalidate = () => queryClient.invalidateQueries(["duty_travel", duty_travel_id]);

  // ── Branch Head: Approve / Decline ───────────────────────────────────────
  const [branchHeadDecision, setBranchHeadDecision] = useState("");
  const statusMutation = useMutation(
    (status) => new API().updateDutyTravelStatus(duty_travel_id, { status }),
    { onSuccess: (d) => { if (d) { invalidate(); toast.success("Decision saved."); } else toast.error("Failed to save decision."); } }
  );
  const handleBranchHeadSubmit = () => {
    if (!branchHeadDecision) { toast.warning("Select Approve or Decline."); return; }
    statusMutation.mutate(branchHeadDecision);
  };

  // ── Acquittal Step 1: Employee return ────────────────────────────────────
  const [returnFile, setReturnFile] = useState(null);
  const [returnConfirmed, setReturnConfirmed] = useState(false);
  const step1Mutation = useMutation(
    (url) => new API().dutyTravelStep1(duty_travel_id, { return_document_url: url }),
    { onSuccess: (d) => { if (d) { invalidate(); toast.success("Return document submitted."); } else toast.error("Failed."); } }
  );
  const handleReturnSubmit = () => {
    if (!returnFile)      { toast.warning("Upload supporting document."); return; }
    if (!returnConfirmed) { toast.warning("Tick the confirmation checkbox."); return; }
    // File is already uploaded to Firebase Storage; returnFile holds the URL
    step1Mutation.mutate(typeof returnFile === "string" ? returnFile : returnFile.name);
  };

  // ── Acquittal Step 2: Acquittal Officer entry ────────────────────────────
  const [acquittalEntry, setAcquittalEntry] = useState({
    departure_datetime: "",
    arrival_datetime: "",
    paid_amount: "",
  });
  const step2Mutation = useMutation(
    (data) => new API().dutyTravelStep2(duty_travel_id, data),
    { onSuccess: (d) => { if (d) { invalidate(); toast.success("Acquittal details saved."); } else toast.error("Failed."); } }
  );
  const handleAcquittalEntrySubmit = () => {
    if (!acquittalEntry.departure_datetime || !acquittalEntry.arrival_datetime) {
      toast.warning("Enter departure and arrival date/times."); return;
    }
    if (!acquittalEntry.paid_amount) { toast.warning("Enter paid amount."); return; }
    step2Mutation.mutate({
      actual_departure_datetime: acquittalEntry.departure_datetime,
      actual_arrival_datetime: acquittalEntry.arrival_datetime,
      paid_amount: Number(acquittalEntry.paid_amount),
    });
  };

  // ── Acquittal Step 3: Employee uploads receipt ───────────────────────────
  const [receiptFile, setReceiptFile] = useState(null);
  const step3Mutation = useMutation(
    (url) => new API().dutyTravelStep3(duty_travel_id, { receipt_url: url }),
    { onSuccess: (d) => { if (d) { invalidate(); toast.success("Receipt uploaded."); } else toast.error("Failed."); } }
  );
  const handleReceiptSubmit = () => {
    if (!receiptFile) { toast.warning("Upload receipt file."); return; }
    step3Mutation.mutate(typeof receiptFile === "string" ? receiptFile : receiptFile.name);
  };

  // ── Acquittal Step 4: Acquittal Officer final review ────────────────────
  const [lineItems, setLineItems] = useState([]);
  const [lineItemFiles, setLineItemFiles] = useState({});
  const step4Mutation = useMutation(
    (items) => new API().dutyTravelStep4(duty_travel_id, { acquittal_line_items: items }),
    { onSuccess: (d) => { if (d) { invalidate(); toast.success("Acquittal finalised."); } else toast.error("Failed."); } }
  );
  const handleFinalAcquittalSubmit = () => {
    step4Mutation.mutate(lineItems);
  };

  if (isLoading) return (
    <div className="wrapper">
      <div className="sidebar"><Sidebar /></div>
      <div className="content"><p style={{ padding: 32 }}>Loading…</p></div>
    </div>
  );

  return (
    <div className="wrapper">
      <div className="sidebar"><Sidebar /></div>
      <div className="content">
        <div className="profile">
          <div className="profileContainer">
            <div className="profileSection">
              <Link to={backUrl} className="backButtonSection">
                <KeyboardBackspaceIcon className="backButton" />
              </Link>
              <br /><br />

              <div className="container">

                {/* ── Header: Title + Status ── */}
                <div className="row" style={{ alignItems: "center" }}>
                  <div className="col col-sm-8">
                    <h1>Duty Travel Request</h1>
                  </div>
                  <div className="col col-sm-4" style={{ textAlign: "right" }}>
                    <StatusBadge status={record?.status ?? "pending"} />
                  </div>
                </div>
                <hr />

                {/* ── Read-Only Summary ── */}
                <div className="row mt-3">
                  <div className="col col-sm-3">
                    <label className="form-label"><b>Employee No</b></label>
                    <input className="form-control" type="text" value={record?.employee_no ?? "—"} disabled />
                  </div>
                  <div className="col col-sm-3">
                    <label className="form-label"><b>Occup Pos Title</b></label>
                    <input className="form-control" type="text" value={record?.occup_pos_title ?? "—"} disabled />
                  </div>
                  <div className="col col-sm-3">
                    <label className="form-label"><b>Travel Type</b></label>
                    <input className="form-control" type="text" value={record?.travel_type ?? "—"} disabled />
                  </div>
                  <div className="col col-sm-3">
                    <label className="form-label"><b>Number of Days</b></label>
                    <input className="form-control" type="text" value={record?.number_of_days ?? "—"} disabled />
                  </div>
                </div>
                <div className="row mt-3">
                  <div className="col col-sm-3">
                    <label className="form-label"><b>Start Date</b></label>
                    <input className="form-control" type="text" value={record?.start_date ? dayjs(record.start_date).format("DD/MM/YYYY") : "—"} disabled />
                  </div>
                  <div className="col col-sm-3">
                    <label className="form-label"><b>End Date</b></label>
                    <input className="form-control" type="text" value={record?.end_date ? dayjs(record.end_date).format("DD/MM/YYYY") : "—"} disabled />
                  </div>
                  <div className="col col-sm-3">
                    <label className="form-label"><b>Resumption Day</b></label>
                    <input className="form-control" type="text" value={record?.resumption_day ? dayjs(record.resumption_day).format("DD/MM/YYYY") : "—"} disabled />
                  </div>
                  <div className="col col-sm-3">
                    <label className="form-label"><b>Cash Advance (PGK)</b></label>
                    <input className="form-control" type="text" value={record?.cash_advance_amount ?? "—"} disabled />
                  </div>
                </div>
                <div className="row mt-3">
                  <div className="col col-sm-12">
                    <label className="form-label"><b>Purpose / Description</b></label>
                    <textarea className="form-control" rows={2} value={record?.purpose ?? "—"} disabled />
                  </div>
                </div>

                <hr className="mt-4" />

                {/* ════════════════════════════════════════════════════════════
                    SECTION A: BRANCH HEAD APPROVAL
                    Visible to: Branch Head user type
                    Action: Approve or Decline the duty travel request
                ════════════════════════════════════════════════════════════ */}
                {/* TODO: show only when record.status === 'pending' AND user is Branch Head */}
                <div style={{ marginTop: 24, padding: "20px 24px", border: "1px solid #e5e7eb", borderRadius: 8, background: "#fafafa" }}>
                  <h5 style={{ marginBottom: 16, color: "#374151" }}>Branch Head Decision</h5>
                  <div className="row">
                    <div className="col col-sm-4">
                      <label className="form-label"><b>Decision</b></label>
                      <TextField
                        select fullWidth size="small"
                        value={branchHeadDecision}
                        onChange={(e) => setBranchHeadDecision(e.target.value)}
                      >
                        <MenuItem value="">— Select —</MenuItem>
                        <MenuItem value="approved">Approve</MenuItem>
                        <MenuItem value="declined">Decline</MenuItem>
                      </TextField>
                    </div>
                    <div className="col col-sm-4" style={{ display: "flex", alignItems: "flex-end" }}>
                      <button type="button" className="saveBtn btn btn-primary" onClick={handleBranchHeadSubmit}>
                        Submit Decision
                      </button>
                      {/* TODO: on approval → email employee; on decline → email employee */}
                    </div>
                  </div>
                </div>

                <hr className="mt-4" />

                {/* ════════════════════════════════════════════════════════════
                    SECTION B: RESUME WORK / ACQUITTAL WORKFLOW
                    Only visible after record.status === 'approved'
                ════════════════════════════════════════════════════════════ */}
                <h4 style={{ marginTop: 24, color: "#374151" }}>Acquittal Workflow</h4>
                <p style={{ fontSize: 13, color: "#6b7280", marginBottom: 16 }}>
                  4-step process once the employee returns from duty travel.
                </p>

                {/* ── Step 1: Employee — Return from Duty Travel ── */}
                <div style={{ marginBottom: 16, padding: "16px 20px", border: "1px solid #ddd", borderRadius: 8 }}>
                  <h6 style={{ color: "#7c3aed" }}>Step 1 — Employee: Return from Duty Travel</h6>
                  <p style={{ fontSize: 13, color: "#6b7280" }}>Upload boarding pass or supporting document to confirm return.</p>
                  <div className="row">
                    <div className="col col-sm-6">
                      <label className="form-label"><b>Upload Document</b> (PDF, JPEG, PNG, TIFF, MS Doc — max 10MB)</label>
                      <input
                        type="file" className="form-control"
                        accept=".pdf,.jpg,.jpeg,.png,.tiff,.doc,.docx"
                        onChange={(e) => setReturnFile(e.target.files[0])}
                      />
                    </div>
                  </div>
                  <div className="row mt-2">
                    <div className="col col-sm-12">
                      <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
                        <input
                          type="checkbox" checked={returnConfirmed}
                          onChange={(e) => setReturnConfirmed(e.target.checked)}
                        />
                        <span style={{ fontSize: 13 }}>I confirm I have returned from duty travel and the above document is correct.</span>
                      </label>
                    </div>
                  </div>
                  <button
                    type="button" className="saveBtn btn btn-primary mt-2"
                    disabled={!returnConfirmed}
                    onClick={handleReturnSubmit}
                  >
                    Submit Return
                  </button>
                  {/* TODO: email Acquittal Officer on submit */}
                </div>

                {/* ── Step 2: Acquittal Officer — Enter Travel Details ── */}
                <div style={{ marginBottom: 16, padding: "16px 20px", border: "1px solid #ddd", borderRadius: 8 }}>
                  <h6 style={{ color: "#7c3aed" }}>Step 2 — Acquittal Officer: Enter Travel Details & Calculate Allowance</h6>
                  <p style={{ fontSize: 13, color: "#6b7280" }}>
                    Enter actual departure/arrival times. System auto-calculates expected allowance and refund amount.
                  </p>
                  <div className="row">
                    <div className="col col-sm-3">
                      <label className="form-label"><b>Departure Date & Time</b></label>
                      <input
                        className="form-control" type="datetime-local"
                        value={acquittalEntry.departure_datetime}
                        onChange={(e) => setAcquittalEntry({ ...acquittalEntry, departure_datetime: e.target.value })}
                      />
                    </div>
                    <div className="col col-sm-3">
                      <label className="form-label"><b>Arrival Date & Time</b></label>
                      <input
                        className="form-control" type="datetime-local"
                        value={acquittalEntry.arrival_datetime}
                        onChange={(e) => setAcquittalEntry({ ...acquittalEntry, arrival_datetime: e.target.value })}
                      />
                    </div>
                    <div className="col col-sm-3">
                      <label className="form-label">
                        <b>Paid Amount ({record?.travel_type === "Overseas" ? "USD" : "PGK"})</b>
                      </label>
                      <input
                        className="form-control" type="number" step="0.01"
                        value={acquittalEntry.paid_amount}
                        onChange={(e) => setAcquittalEntry({ ...acquittalEntry, paid_amount: e.target.value })}
                      />
                    </div>
                  </div>
                  <div className="row mt-3">
                    <div className="col col-sm-3">
                      <label className="form-label"><b>Expected Allowance (Auto)</b></label>
                      <input className="form-control" type="text" value="— auto-calculated —" disabled />
                      {/* TODO: calculate from domestic rates table or OSTA rates by country + number of days + departure time */}
                    </div>
                    <div className="col col-sm-3">
                      <label className="form-label"><b>Refund Amount (Auto)</b></label>
                      <input className="form-control" type="text" value="— auto-calculated —" disabled />
                      {/* TODO: Paid Amount − Expected Allowance */}
                    </div>
                  </div>
                  <button type="button" className="saveBtn btn btn-primary mt-2" onClick={handleAcquittalEntrySubmit}>
                    Save & Notify Employee
                  </button>
                  {/* TODO: email Employee to upload receipt */}
                </div>

                {/* ── Step 3: Employee — Upload Receipt ── */}
                <div style={{ marginBottom: 16, padding: "16px 20px", border: "1px solid #ddd", borderRadius: 8 }}>
                  <h6 style={{ color: "#7c3aed" }}>Step 3 — Employee: Upload Receipt</h6>
                  <p style={{ fontSize: 13, color: "#6b7280" }}>Upload receipt for the refund amount.</p>
                  <div className="row">
                    <div className="col col-sm-6">
                      <label className="form-label"><b>Receipt File</b> (PDF, JPEG, PNG, TIFF — max 10MB)</label>
                      <input
                        type="file" className="form-control"
                        accept=".pdf,.jpg,.jpeg,.png,.tiff"
                        onChange={(e) => setReceiptFile(e.target.files[0])}
                      />
                    </div>
                  </div>
                  <button type="button" className="saveBtn btn btn-primary mt-2" onClick={handleReceiptSubmit}>
                    Upload Receipt
                  </button>
                  {/* TODO: email Acquittal Officer on upload */}
                </div>

                {/* ── Step 4: Acquittal Officer — Final Review ── */}
                <div style={{ marginBottom: 16, padding: "16px 20px", border: "1px solid #ddd", borderRadius: 8 }}>
                  <h6 style={{ color: "#7c3aed" }}>Step 4 — Acquittal Officer: Final Review</h6>
                  <p style={{ fontSize: 13, color: "#6b7280" }}>
                    Review each line item. For any "No", upload supporting document. Submit to generate final report.
                  </p>
                  {/* TODO: render line items from record; each row has Yes/No toggle + optional file upload for "No" items */}
                  <div style={{ color: "#9ca3af", fontSize: 13, fontStyle: "italic" }}>
                    Line items will appear here once Step 2 is completed.
                  </div>
                  <button type="button" className="saveBtn btn btn-primary mt-3" onClick={handleFinalAcquittalSubmit}>
                    Submit Final Acquittal
                  </button>
                  {/* TODO: email Employee; unlock Print Report button */}
                </div>

                {/* ── Print Report (unlocked after Step 4 complete) ── */}
                <div style={{ marginTop: 12 }}>
                  <button
                    type="button" className="saveBtn btn btn-secondary"
                    disabled={true /* TODO: enable after acquittal complete */}
                    title="Available after acquittal is finalised"
                  >
                    Print Acquittal Report
                  </button>
                  {/* TODO: generate Domestic or Overseas Acquittal Form PDF (see form templates in spec) */}
                </div>

              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DutyTravelView;
